import React, { useState, useEffect } from 'react';
import { Link, useLocation, Outlet, Navigate } from 'react-router-dom';
import { frontendDb as db } from '../../lib/frontend-db';
import AboutPage from './AboutPage';
import PostersPage from './PostersPage';
import VereinePage from './VereinePage';
import StimmArtPage from './StimmArtPage';
import YoungStarsPage from './YoungStarsPage';
import WiesenInfoPage from './WiesenInfoPage';
import BieberInfoPage from './BieberInfoPage';
import NewsPage from './NewsPage';
import SlideshowPage from './SlideshowPage';
import SectionsPage from './SectionsPage';
import GalleryPage from './GalleryPage';
import ContactPage from './ContactPage';
import ContactFormPage from './ContactFormPage';
import SettingsPage from './SettingsPage';
import FontsPage from './FontsPage';
import ImprintPage from './ImprintPage';
import PrivacyPage from './PrivacyPage';

interface Tab {
  id: string;
  path: string;
  label: string;
}

const defaultTabs: Tab[] = [
  { id: 'about', path: '/about', label: 'Über uns' },
  { id: 'posters', path: '/posters', label: 'Veranstaltungsplakate' },
  { id: 'vereine', path: '/vereine', label: 'Vereine' },
  { id: 'stimmart', path: '/stimmart', label: 'StimmArt' },
  { id: 'youngstars', path: '/youngstars', label: 'YoungStars' },
  { id: 'wiesen-info', path: '/wiesen-info', label: 'Liederkranz Wiesen' },
  { id: 'bieber-info', path: '/bieber-info', label: 'Liederkranz Bieber' },
  { id: 'news', path: '/news', label: 'Nachrichten' },
  { id: 'slideshow', path: '/slideshow', label: 'Startbilder' },
  { id: 'sections', path: '/sections', label: 'Inhaltsbereiche' },
  { id: 'gallery', path: '/gallery', label: 'Bildergalerie' },
  { id: 'contact-form', path: '/contact-form', label: 'Kontaktformular' },
  { id: 'contact', path: '/contact', label: 'Kontaktdaten' },
  { id: 'fonts', path: '/fonts', label: 'Schriftarten' },
  { id: 'settings', path: '/settings', label: 'Website-Einstellungen' },
  { id: 'imprint', path: '/imprint', label: 'Impressum' },
  { id: 'privacy', path: '/privacy', label: 'Datenschutz' },
];

const DashboardLayout = () => {
  const location = useLocation();
  const currentPath = location.pathname;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabs, setTabs] = useState<Tab[]>(defaultTabs);



  useEffect(() => {
    // Check authentication
    const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
    if (!isAuthenticated) {
      window.location.href = '/admin';
      return;
    }
    
    loadMenuTitles();
    
    // Listen for settings changes to update menu titles
    const handleSettingsChange = () => {
      console.log('🔄 DashboardLayout: Settings updated event received');
      loadMenuTitles();
    };

    window.addEventListener('settingsUpdated', handleSettingsChange);
    
    return () => {
      window.removeEventListener('settingsUpdated', handleSettingsChange);
    };
  }, []);

  const loadMenuTitles = async () => {
    try {
      // Load dynamic titles from database
      const [settings, about, contact] = await Promise.all([
        db.getSettings(),
        db.getAbout(),
        db.getContact()
      ]);

      // Update tabs with dynamic titles
      const updatedTabs = defaultTabs.map(tab => {
        switch (tab.id) {
          case 'about':
            return { ...tab, label: (about as any)?.section_title || 'Das sind wir!' };
          case 'news':
            return { ...tab, label: (settings as any)?.news_title || 'Aktuelles' };
          case 'gallery':
            return { ...tab, label: (settings as any)?.gallery_title || 'Galerie' };
          case 'posters':
            return { ...tab, label: (settings as any)?.posters_title || 'Aktuelles Konzert' };
          case 'vereine':
            return { ...tab, label: (settings as any)?.vereine_title || 'Unsere Vereine' };
          case 'contact':
            return { ...tab, label: (contact as any)?.section_title || 'Kontakt' };
          default:
            return tab;
        }
      });

      setTabs(updatedTabs);
    } catch (error) {
      console.error('Error loading menu titles:', error);
      // Keep default tabs on error
    } finally {
      setLoading(false);
    }
  };

  const isActive = (path: string) => {
    // Exakte Übereinstimmung oder beginnt mit dem Pfad gefolgt von /
    // Dies verhindert dass /contact auch /contact-form matched
    const fullPath = `/admin/dashboard${path}`;
    return currentPath === fullPath || currentPath.startsWith(fullPath + '/');
  };

  const handleLogout = () => {
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('sessionStart');
    window.location.href = '/admin';
  };

  if (!loading && location.pathname === '/admin/dashboard') {
    return <Navigate to={`/admin/dashboard${tabs[0].path}`} replace />;
  }

  if (loading) {
    return <div className="min-h-screen bg-green-50 p-4 animate-pulse">Lädt...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-100 p-4 admin-dashboard">
      <div className="max-w-6xl mx-auto text-gray-900">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-2xl font-bold text-gray-800">Verwaltungsbereich</h1>
          <button
            onClick={handleLogout}
            className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 transition-colors"
          >
            Abmelden
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
            {error}
          </div>
        )}

        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-3">
            <div className="bg-white rounded-lg shadow-md p-4">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Menü</h2>
              <nav className="space-y-2">
                {tabs.map((tab) => (
                  <Link
                    key={tab.id}
                    to={`/admin/dashboard${tab.path}`}
                    className={`block w-full px-4 py-3 rounded-md text-sm font-medium transition-colors ${
                      isActive(tab.path)
                        ? 'bg-blue-100 text-blue-800 border-l-4 border-blue-500'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    {tab.label}
                  </Link>
                ))}
              </nav>
            </div>
          </div>

          <div className="col-span-12 md:col-span-9">
            {isActive('/about') && <AboutPage />}
            {isActive('/posters') && <PostersPage />}
            {isActive('/vereine') && <VereinePage />}
            {isActive('/stimmart') && <StimmArtPage />}
            {isActive('/youngstars') && <YoungStarsPage />}
            {isActive('/wiesen-info') && <WiesenInfoPage />}
            {isActive('/bieber-info') && <BieberInfoPage />}
            {isActive('/news') && <NewsPage />}
            {isActive('/slideshow') && <SlideshowPage />}
            {isActive('/sections') && <SectionsPage />}
            {isActive('/gallery') && <GalleryPage />}
            {isActive('/contact-form') && <ContactFormPage />}
            {isActive('/contact') && <ContactPage />}
            {isActive('/fonts') && <FontsPage />}
            {isActive('/settings') && <SettingsPage />}
            {isActive('/imprint') && <ImprintPage />}
            {isActive('/privacy') && <PrivacyPage />}
            {!isActive('/about') && !isActive('/posters') && !isActive('/vereine') && 
             !isActive('/stimmart') && !isActive('/youngstars') && !isActive('/wiesen-info') && !isActive('/bieber-info') && 
             !isActive('/news') && !isActive('/slideshow') && !isActive('/sections') && 
             !isActive('/gallery') && !isActive('/contact') && !isActive('/contact-form') && !isActive('/fonts') && !isActive('/settings') && 
             !isActive('/imprint') && !isActive('/privacy') && <Outlet />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardLayout;